import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { ticketKeys } from './keys'
import { awaitingRedraft } from '@/lib/agentItems'
import type { AgentAnswerAction, AgentItem, AgentPanel } from '@/types/agent'

// Polled until real-time events land (the plan's PR 4): quickly while the agent is at work or waiting on staff, slowly
// otherwise.
const ACTIVE_POLL_MS = 5_000
const IDLE_POLL_MS = 30_000

export const isAgentBusy = (panel: AgentPanel | undefined) =>
  Boolean(panel && (panel.claim || panel.items.some(item => item.status === 'pending' || awaitingRedraft(item, panel.items))))

/** Whether Agent Relay has touched this ticket at all. */
export const hasAgentRelayActivity = (panel: AgentPanel | undefined) =>
  Boolean(panel && (panel.claim || panel.stoodDown || panel.items.length || panel.activity.length))

export function useAgentPanel(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.agent(ticketId),
    queryFn: () => api<AgentPanel>(`/support-tickets/${ticketId}/agent`),
    enabled,
    refetchInterval: query => (isAgentBusy(query.state.data) ? ACTIVE_POLL_MS : IDLE_POLL_MS),
  })
}

export interface AnswerVars {
  questionId: string
  action: AgentAnswerAction
  text?: string
  reason?: string
}

export function useAnswerAgentItem(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ questionId, ...body }: AnswerVars) =>
      api<{ item: AgentItem }>(`/support-tickets/${ticketId}/agent-items/${encodeURIComponent(questionId)}/answer`, { method: 'POST', body }),
    // A 409 (answered elsewhere first) also changed the mirror, so the panel refetches either way.
    onSettled: () => qc.invalidateQueries({ queryKey: ticketKeys.agent(ticketId) }),
  })
}

export function useBringAgentBack(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api(`/support-tickets/${ticketId}/agent/bring-back`, { method: 'POST' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ticketKeys.agent(ticketId) }),
  })
}
